import * as Sentry from '@sentry/react';

/**
 * Service Worker Registration Utility
 * 
 * Handles PWA registration, updates, and offline capability detection.
 */

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    console.log('Service Worker registered:', registration.scope);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available
            console.log('New version available!');
            showUpdateNotification();
          }
        });
      }
    });

    // Check for updates periodically
    const intervalId = setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000); // Check every hour

    // Store interval ID for cleanup
    (registration as any).__updateIntervalId = intervalId;

  } catch (error) {
    console.error('Service Worker registration failed:', error);
    Sentry.captureException(error, { extra: { context: 'Registering service worker' } });
  }
}

/**
 * Unregister service worker (useful for debugging)
 */
export async function unregisterServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  await registration.unregister();
  console.log('Service Worker unregistered');
}

/**
 * Check if app is in standalone mode (installed PWA)
 */
export function isStandaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         // @ts-ignore - iOS property
         window.navigator.standalone === true;
}

/**
 * Check if app is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Listen for online/offline events
 */
export function listenForConnectivityChanges(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * Show update notification
 */
function showUpdateNotification(): void {
  // You could dispatch a custom event or use a toast notification
  const event = new CustomEvent('sw-update-available', {
    detail: { message: 'A new version is available. Refresh to update.' }
  });
  window.dispatchEvent(event);
}

/**
 * Request background sync permission
 */
export async function requestBackgroundSync(tag: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('sync' in (navigator as any).serviceWorker)) {
    console.log('Background sync not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    // @ts-ignore - Background sync API
    await registration.sync.register(tag);
    console.log('Background sync registered:', tag);
  } catch (error) {
    console.error('Background sync failed:', error);
    Sentry.captureException(error, { extra: { context: 'Requesting background sync' } });
  }
}

/**
 * Get PWA install prompt
 */
export function listenForInstallPrompt(
  callback: (promptEvent: BeforeInstallPromptEvent) => void
): () => void {
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  const handler = (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    callback(deferredPrompt);
  };

  window.addEventListener('beforeinstallprompt', handler);

  return () => {
    window.removeEventListener('beforeinstallprompt', handler);
  };
}

// Type definition for BeforeInstallPromptEvent
declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
}

/**
 * Check PWA capabilities
 */
export function getPWACapabilities(): {
  serviceWorker: boolean;
  pushNotifications: boolean;
  backgroundSync: boolean;
  offlineStorage: boolean;
} {
  return {
    serviceWorker: 'serviceWorker' in navigator,
    pushNotifications: 'PushManager' in window,
    backgroundSync: 'sync' in (navigator as any).serviceWorker || false,
    offlineStorage: 'storage' in navigator && 'estimate' in navigator.storage,
  };
}
